import HelperText from '../helperText/HelperText';
import { Tick } from '../icons';
import InlineMessage from '../inlineMessage';
import styles from './Checkbox.module.scss';
import { useCheckbox } from './useCheckbox';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { forwardRef, useRef } from 'react';

const checkedIcon = <Tick width="20" height="20" color="black" />;

export const Checkbox = forwardRef((props, ref) => {
  const {
    ariaLabel,
    autoComplete,
    field,
    id,
    checkStatus,
    defaultChecked,
    name,
    label,
    disabled,
    isRequired,
    readOnly,
    onChange,
    onBlur,
    onFocus,
    onKeyInput,
    helpText,
    error,
    variant,
    errorMessage,
    ariaDescribedBy: ariaDescribedByProp,
    customLabelStyle,
    value,
    className,
    dataAttributes,
    children,
  } = props;
  const inputNode = useRef(null);

  const { handleChange, isChecked, handleKeyControl, stopPropagation, toggle } = useCheckbox({
    disabled,
    readOnly,
    onChange,
    field,
    ref,
    inputNode,
    onBlur,
    checkStatus,
    defaultChecked,
    onKeyInput,
  });

  const describedBy = [];

  if (error && typeof error !== 'boolean') {
    describedBy.push(`${id}ErrorText`);
  }
  if (helpText) {
    describedBy.push(`${id}HelpText`);
  }
  if (ariaDescribedByProp) {
    describedBy.push(ariaDescribedByProp);
  }
  const ariaDescribedBy = describedBy.length ? describedBy.join(' ') : undefined;

  const isIndeterminate = isChecked === 'indeterminate';
  const icon = isChecked ? checkedIcon : null;
  const inverse = isChecked ? styles.inverse : styles.inverseUnchecked;
  const indeterminateAttributes = isIndeterminate
    ? { indeterminate: 'true', 'aria-checked': 'mixed' }
    : { 'aria-checked': isChecked };
  const input_id = field && field.id ? field.id : id;
  return (
    <div className={clsx(styles.rootWrapper)}>
      <label
        aria-label={ariaLabel}
        onKeyDown={handleKeyControl}
        onClick={toggle}
        onFocus={(e) => onFocus(e)}
        className={clsx(
          styles.root,
          {
            [` ${styles.error}`]: error,
            [` ${styles.boxed}`]: variant === 'boxed',
            [` ${styles.disabled}`]: disabled,
            [` ${styles.readOnly}`]: readOnly,
            [` ${styles.centeredLabel}`]: !!customLabelStyle,
          },
          styles.position,
          className,
        )}
        htmlFor={id}
        tabIndex={disabled ? undefined : 0}
        {...dataAttributes}
      >
        <div className={styles.control}>
          <input
            {...field}
            id={input_id}
            name={field && field.name ? field.name : name}
            disabled={disabled}
            ref={(inputRef) => {
              inputNode.current = inputRef;
              ref && ref instanceof Function ? ref(inputRef) : inputRef;
            }}
            autoComplete={autoComplete}
            checked={isChecked}
            required={isRequired}
            onClick={stopPropagation}
            onChange={handleChange}
            type="checkbox"
            aria-invalid={error !== null}
            aria-describedby={ariaDescribedBy}
            className={`${styles.input} ${styles.inverse}`}
            value={value}
            {...indeterminateAttributes}
          />
          {variant === 'inverse' ? (
            <span className={`${styles.icon} ${inverse}`}>{icon}</span>
          ) : (
            <span className={`${styles.icon}`}>{icon}</span>
          )}
        </div>
        {typeof label === 'string' ? (
          <span className={styles.description}>
            <span className={customLabelStyle || styles.label}>{label}</span>
            {helpText ? <HelperText>{helpText}</HelperText> : null}
          </span>
        ) : (
          children
        )}
      </label>
      {error && errorMessage ? <InlineMessage type="error" message={errorMessage} /> : null}
    </div>
  );
});

Checkbox.propTypes = {
  ariaLabel: PropTypes.string,
  autoComplete: PropTypes.string,
  id: PropTypes.string,
  checkStatus: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  isRequired: PropTypes.bool,
  helpText: PropTypes.string,
  error: PropTypes.bool,
  variant: PropTypes.oneOf(['boxed', 'regular', 'inverse']),
  errorMessage: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  className: PropTypes.string,
  onKeyInput: PropTypes.func,
  onFocus: PropTypes.func,
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  dataAttributes: PropTypes.object,
};

Checkbox.defaultProps = {
  variant: 'regular',
  autoComplete: '',
  checkStatus: false,
  defaultChecked: false,
  onKeyInput: () => {},
  onFocus: () => {},
  isRequired: false,
  size: 'large',
};

Checkbox.displayName = 'Checkbox';
